import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { ListSearch, ListToolbar } from "@/components/ui/list-toolbar";
import { PageHeader, SectionHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";

import AddNewFormatModal from "../../../components/exhibitListFormats/AddNewFormatModal";
import { FormatCard } from "../../../components/exhibitListFormats/ExhibitListFormat";
import { ListFormatProvider } from "../../../contexts/ListFormatContext";
import { ConfirmationDialog } from "../../../components/common/ConfimationDialog";
import useFirestoreCollection from "../../../hooks/useFirestoreCollection";
import { ExhibitListFormat } from "../../../types/exhibit_formats";
import { DATABASE } from "../../../types/tables-data";

const FormatTile: FC<{
  format: ExhibitListFormat;
  onEdit: () => void;
  onDelete?: () => void;
}> = ({ format, onEdit, onDelete }) => (
  <div className="flex flex-col items-start gap-1.5">
    <FormatCard format={format} isSelected={false} onClick={() => {}} />
    <span className="max-w-30 truncate text-sm text-fg-2">
      {format.formatName}
    </span>
    <div className="flex gap-1">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Edit format"
        onClick={onEdit}
      >
        <Pencil />
      </Button>
      {onDelete ? (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Delete format"
          className="text-danger"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 />
        </Button>
      ) : null}
    </div>
  </div>
);

const AdminExhibitListFormatsContent = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFormat, setSelectedFormat] =
    useState<ExhibitListFormat | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [formatPendingDelete, setFormatPendingDelete] =
    useState<ExhibitListFormat | null>(null);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const isDeleteOpen = formatPendingDelete !== null;
  const onDeleteClose = () => setFormatPendingDelete(null);

  const {
    documents: formats,
    loading,
    deleteDocument
  } = useFirestoreCollection<ExhibitListFormat>(DATABASE.EXHIBIT_LIST_FORMATS, {
    listen: true,
    useLawyerAccessMap: true,
    enableToast: true
  });

  const filteredFormats = searchQuery
    ? formats?.filter((format) =>
        format.formatName?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : formats;

  const defaultFormat = filteredFormats?.find(
    (format) => format.id === "default"
  );
  const otherFormats = filteredFormats?.filter(
    (format) => format.id !== "default"
  );

  const handleEditFormat = (format: ExhibitListFormat) => {
    setSelectedFormat(format);
    onOpen();
  };

  const handleCloseModal = () => {
    setSelectedFormat(null);
    onClose();
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-y-auto bg-surface">
      <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Exhibit List Formats"
          count={formats?.length ?? null}
          actions={
            <Button
              size="sm"
              onClick={() => {
                setSelectedFormat(null);
                onOpen();
              }}
            >
              <Plus /> New Format
            </Button>
          }
        />
        <ListToolbar>
          <ListSearch
            aria-label="Search formats"
            placeholder="Search Formats"
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
        </ListToolbar>
      </div>

      <div className="flex flex-col gap-8 px-6 pt-3 pb-16">
        {loading ? (
          <div className="flex flex-wrap gap-6">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-40 w-30 rounded-lg" />
            ))}
          </div>
        ) : (
          <>
            {defaultFormat ? (
              <section className="flex flex-col gap-3">
                <SectionHeader title="Default Format" />
                <FormatTile
                  format={defaultFormat}
                  onEdit={() => handleEditFormat(defaultFormat)}
                />
              </section>
            ) : null}
            <section className="flex flex-col gap-3">
              <SectionHeader title="Custom Formats" />
              <div className="grid grid-cols-2 justify-items-start gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {otherFormats?.map((format) => (
                  <FormatTile
                    key={format.id}
                    format={format}
                    onEdit={() => handleEditFormat(format)}
                    onDelete={() => setFormatPendingDelete(format)}
                  />
                ))}
              </div>
            </section>
          </>
        )}
      </div>

      <AddNewFormatModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        initialData={selectedFormat}
      />
      <ConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          onDeleteClose();
        }}
        onConfirm={() => {
          if (formatPendingDelete?.id) {
            deleteDocument(formatPendingDelete.id);
          }
          onDeleteClose();
        }}
        title="Delete Format"
        body={
          formatPendingDelete
            ? `Are you sure you want to delete "${formatPendingDelete.formatName}"?`
            : "Are you sure you want to delete this format?"
        }
      />
    </div>
  );
};

export const AdminExhibitListFormats = () => {
  return (
    <ListFormatProvider>
      <AdminExhibitListFormatsContent />
    </ListFormatProvider>
  );
};
